// Self-contained client for pos-fleet-support-service. Copies
// `deviceConfigApi.ts` conventions exactly (see that file): a pure host
// resolver exported for tests, `authHeader()` reading the raw JWT from
// `localStorage.jwtToken` with NO "Bearer " prefix, and a `call<T>()` that
// throws on `!res.ok`.
//
// Authorization header form: this service's endpoints (`createCommand`,
// `listCommands`, `cancelCommand`) all declare `authorizer: { type:
// COGNITO_USER_POOLS }` with the default identity source
// (`method.request.header.Authorization`) — byte-for-byte the same
// declaration `device-config-service` uses for its own admin routes, which
// `deviceConfigApi.ts` already calls successfully in production with the raw
// token, no "Bearer " prefix. API Gateway's built-in COGNITO_USER_POOLS
// authorizer validates that header's value AS the JWT; it does not strip a
// scheme prefix unless the identitySource mapping says to, and neither
// service's serverless.yml does. So this client matches `deviceConfigApi.ts`
// rather than `apiService.ts`'s `Bearer ${jwtToken}` — using `Bearer` here
// would send a Cognito authorizer of this exact kind a token it cannot parse.
//
// Unverified against live hardware/a real admin session (no super-admin JWT
// was obtainable for a curl at authoring time — the dev test-admin is
// MFA-locked). If a 401 shows up in the browser where a 200 was expected,
// this is the first thing to flip.
//
// `pos-fleet-support-service` rides the shared `platform.*` cluster domain
// with `basePath: fleet` (Global Constraints) rather than getting its own
// subdomain like device-config-service does — so the host is
// `platform.api.<stage>.<apex>` with `/fleet` appended, not
// `<service>.api.<stage>.<apex>`.

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/**
 * Pure host resolver — exported for tests.
 *
 * localhost/127.0.0.1 → `/fleet-api` (proxied in dev).
 * Otherwise → `https://platform.api.[stage.]<apex>/fleet`, matching the live
 * dev endpoint this task was briefed against:
 * `https://platform.api.dev.rubytill.com/fleet/...`.
 */
export function resolveFleetHost(hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/fleet-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/fleet`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/fleet`;
  return `https://platform.api.dev.${apex}/fleet`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_FLEET_SUPPORT_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveFleetHost(h, detectStage(), envBase);
};

/** pos-pay-devices-service host (RubyTill Pay live state) — own REGIONAL domain, `pay-devices.api.[stage.]<apex>`. */
export function resolvePayDevicesHost(hostname: string, stage: 'prod' | 'staging' | 'dev'): string {
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  const fallback = apex === 'localhost' || apex.startsWith('127.') ? 'rubytill.com' : apex;
  if (stage === 'prod') return `https://pay-devices.api.${fallback}`;
  if (stage === 'staging') return `https://pay-devices.api.stage.${fallback}`;
  return `https://pay-devices.api.dev.${fallback}`;
}

export interface RtpayLiveEvent { at: string; kind: string; screen?: string; detail?: string }
export interface RtpayLiveState {
  online: boolean;
  lastSeenAt: string | null;
  appVersion: string | null;
  lastScreen: string | null;
  lastScreenAt: string | null;
  recentEvents: RtpayLiveEvent[];
  /** Task 22 — this CPad's RubyTill Pay refund / void locks (pos-payments' stuck-lock sweep). Absent
   *  when pay-devices could not read them. */
  refundLocks?: RtpayRefundLock[];
}

export interface RtpayRefundLock {
  refundRef: string;
  kind: 'refund' | 'void' | 'unreferenced' | string;
  amount: number | null;
  transactionId: string;
  /** lock = pending on the CPad · watch = auto-released, nothing refunded (SUNBAY watched 7 days) ·
   *  late = SUNBAY approved it after the release — recorded as a needs-review orphan refund. */
  mode: 'lock' | 'watch' | 'late' | string;
  createdAt: string | null;
  releasedAt: string | null;
  releaseReason: string | null;
  lateRecordedAt: string | null;
}

/** Exported for tests / callers that need to inspect the raw header shape. */
export function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

/**
 * Thrown by `call()` on a non-OK response. Carries the parsed `error` code
 * (e.g. `device_offline`) when the body is JSON shaped that way, so a caller
 * like `DestructiveConfirm` can branch on the SERVER'S refusal reason rather
 * than string-matching a message. Falls back to the raw text when the body
 * isn't JSON — never swallowed, never turned into a fabricated success.
 */
export class FleetApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, bodyText: string) {
    let code: string | undefined;
    let message = `fleet-support ${status}: ${bodyText}`;
    try {
      const parsed = JSON.parse(bodyText);
      if (parsed && typeof parsed.error === 'string') {
        code = parsed.error;
        message = parsed.message || parsed.error;
      }
    } catch { /* non-JSON body — keep the raw text message */ }
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) throw new FleetApiError(res.status, await res.text().catch(() => ''));
  return res.status === 204 ? (undefined as T) : res.json();
}

import type { FleetActionId, FleetCommand, FleetImpact, FleetAuditEvent } from '@platform/shared-types';

export interface CreateFleetCommandBody {
  deviceId: string;
  deviceType: string;
  action: FleetActionId;
  params?: Record<string, unknown>;
  reason?: string;
  codeId?: string;
  code?: string;
}

export const fleetSupportApi = {
  /** `GET /pay-devices/admin/devices/{tenantId}/{emvDeviceId}/state` on pos-pay-devices-service — the device's live screen and events. */
  getRtpayLiveState: async (tenantId: string, emvDeviceId: string): Promise<RtpayLiveState> => {
    const h = typeof window !== 'undefined' ? window.location.hostname : '';
    const res = await fetch(
      `${resolvePayDevicesHost(h, detectStage())}/pay-devices/admin/devices/${encodeURIComponent(tenantId)}/${encodeURIComponent(emvDeviceId)}/state`,
      { headers: { 'Content-Type': 'application/json', ...authHeader() } },
    );
    if (!res.ok) throw new FleetApiError(res.status, await res.text().catch(() => ''));
    return res.json();
  },

  /** `POST /fleet/commands` — returns the command row AS THE SERVER LEFT IT (queued or sent). */
  createCommand: (body: CreateFleetCommandBody) =>
    call<FleetCommand>('/commands', { method: 'POST', body: JSON.stringify(body) }),

  /** `GET /fleet/commands?deviceId=&limit=` — one device's command history, newest first. */
  listCommands: (deviceId: string, limit?: number) =>
    call<{ commands: FleetCommand[] }>(
      `/commands?deviceId=${encodeURIComponent(deviceId)}${limit ? `&limit=${limit}` : ''}`,
    ),

  /**
   * `POST /fleet/commands/{commandId}/cancel?deviceId=` — `deviceId` is
   * REQUIRED as a query param even though it is not in the path: it is the
   * command table's partition key, so the service cannot look the row up
   * without it (confirmed against Task 5's review, 2026-09-20).
   */
  cancelCommand: (commandId: string, deviceId: string) =>
    call<FleetCommand>(
      `/commands/${encodeURIComponent(commandId)}/cancel?deviceId=${encodeURIComponent(deviceId)}`,
      { method: 'POST' },
    ),

  /**
   * `GET /fleet/commands/{commandId}/artifact?deviceId=&name=` — a short-lived
   * signed S3 link to what a `collect_logs` (`logcat.txt`) or `capture_screen`
   * (`screen-main.png` / `screen-panel.png`) command uploaded. The bucket keeps
   * them 7 days; after that S3 answers 404 on the link, never this call.
   */
  getArtifact: (commandId: string, deviceId: string, name: 'logcat.txt' | 'screen-main.png' | 'screen-panel.png' | 'rtpay-log.txt') =>
    call<{ url: string }>(
      `/commands/${encodeURIComponent(commandId)}/artifact?deviceId=${encodeURIComponent(deviceId)}&name=${encodeURIComponent(name)}`,
    ),

  /**
   * `GET /fleet/devices/{deviceId}/impact` — the live blast-radius probe a
   * tier-3 confirm names before it offers the button. `stale: true` means
   * the figure is a remembered one, not one the device just gave back — the
   * confirm's second-confirmation rule keys off this exact flag.
   *
   * Being built in parallel (Global Constraints) — a 404/5xx here is a
   * genuine "cannot verify" state, never treated as "nothing to lose".
   */
  getImpact: (deviceId: string) =>
    call<{ impact: FleetImpact; stale: boolean }>(`/devices/${encodeURIComponent(deviceId)}/impact`),

  /**
   * `POST /fleet/auth-codes` — issues a code bound to this device, this
   * action and this reason. Being built in parallel; a failure here must
   * surface as "could not send the code", never a fabricated success.
   */
  issueAuthCode: (body: { deviceId: string; action: FleetActionId; reason: string }) =>
    call<{ codeId: string; issuedTo: string[]; expiresAt: number }>(
      '/auth-codes',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * `GET /fleet/audit?deviceId=&actor=&kind=&from=&to=&limit=&cursor=` —
   * the append-only trail (Phase 4 view, Task 23). Being built in parallel.
   */
  getAudit: (params: {
    deviceId?: string; actor?: string; kind?: string; from?: number; to?: number;
    limit?: number; cursor?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params.deviceId) qs.set('deviceId', params.deviceId);
    if (params.actor) qs.set('actor', params.actor);
    if (params.kind) qs.set('kind', params.kind);
    if (params.from != null) qs.set('from', String(params.from));
    if (params.to != null) qs.set('to', String(params.to));
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.cursor) qs.set('cursor', params.cursor);
    const q = qs.toString();
    return call<{ events: FleetAuditEvent[]; cursor?: string }>(`/audit${q ? `?${q}` : ''}`);
  },
};
